import React, { useState, useEffect } from 'react';
import { auth, database } from '../firebase/config';
import { ref, set, get, update, push, serverTimestamp, runTransaction } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';



const WithdrawMoney = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankDetails, setBankDetails] = useState(null);
  const [showBankForm, setShowBankForm] = useState(false);
  // UPI functionality removed - Bank transfer only
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [withdrawableAmount, setWithdrawableAmount] = useState(0);
  const [tdsSettings, setTdsSettings] = useState({ enabled: false, percentage: 0 });
  const [adminChargesSettings, setAdminChargesSettings] = useState({ enabled: false, percentage: 0 });

  const [bankForm, setBankForm] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: '',
    accountHolderName: '',
    accountType: 'savings'
  });

  // UPI functionality removed

  const [errors, setErrors] = useState({});

  const cleanObject = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(v => (v && typeof v === 'object') ? cleanObject(v) : v);
    } else if (obj && typeof obj === 'object') {
      const result = {};
      for (const key in obj) {
        if (obj[key] !== undefined && obj[key] !== null) {
          result[key] = (typeof obj[key] === 'object') ? cleanObject(obj[key]) : obj[key];
        }
      }
      return result;
    }
    return obj;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        fetchUserData(user.uid);
        fetchTransactions(user.uid);
        fetchTdsSettings();
        fetchAdminChargesSettings();
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchTdsSettings = async () => {
    try {
      const tdsRef = ref(database, 'HTAMS/settings/tds');
      const snapshot = await get(tdsRef);
      if (snapshot.exists()) {
        setTdsSettings(snapshot.val());
      }
    } catch (error) {
      console.error('Error fetching TDS settings:', error);
    }
  };

  const fetchAdminChargesSettings = async () => {
    try {
      const adminChargesRef = ref(database, 'HTAMS/settings/adminCharges');
      const snapshot = await get(adminChargesRef);
      if (snapshot.exists()) {
        setAdminChargesSettings(snapshot.val());
      }
    } catch (error) {
      console.error('Error fetching admin charges settings:', error);
    }
  };

  const fetchUserData = async (uid) => {
    try {
      const userRef = ref(database, `HTAMS/users/${uid}`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        setUserData(data);

        // Withdrawable Amount = Current MySales balance
        const currentBalance = parseFloat(data.MySales || 0);
        setWithdrawableAmount(currentBalance);

        const bankRef = ref(database, `HTAMS/users/${uid}/bankDetails`);
        const bankSnapshot = await get(bankRef);
        if (bankSnapshot.exists()) {
          setBankDetails(bankSnapshot.val());
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (uid) => {
    try {
      const transactionsRef = ref(database, `HTAMS/users/${uid}/transactions`);
      const snapshot = await get(transactionsRef);
      if (snapshot.exists()) {
        const transactionsData = snapshot.val();
        const transactionsArray = Object.entries(transactionsData).map(([id, data]) => ({
          id,
          ...data
        })).sort((a, b) => b.timestamp - a.timestamp);

        setTransactions(transactionsArray);

        const totalWithdrawnAmount = transactionsArray
          .filter(t => t.status === 'approved' && t.type === 'withdrawal')
          .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

        const pendingWithdrawalsAmount = transactionsArray
          .filter(t => t.status === 'pending' && t.type === 'withdrawal')
          .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

        setTotalWithdrawn(totalWithdrawnAmount);
        setPendingWithdrawals(pendingWithdrawalsAmount);
      } else {
        setTransactions([]);
        setTotalWithdrawn(0);
        setPendingWithdrawals(0);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
      setTotalWithdrawn(0);
      setPendingWithdrawals(0);
    }
  };

  useEffect(() => {
    if (userData && userData.MySales !== undefined) {
      const currentBalance = parseFloat(userData.MySales || 0);
      setWithdrawableAmount(currentBalance);
    }
  }, [userData]);

  const validateBankForm = () => {
    const newErrors = {};

    if (!bankForm.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(bankForm.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 9-18 digits';
    }

    if (!bankForm.confirmAccountNumber.trim()) {
      newErrors.confirmAccountNumber = 'Please confirm account number';
    } else if (bankForm.accountNumber !== bankForm.confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Account numbers do not match';
    }

    if (!bankForm.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankForm.ifscCode.toUpperCase())) {
      newErrors.ifscCode = 'Invalid IFSC code format';
    }

    if (!bankForm.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }

    if (!bankForm.branchName.trim()) {
      newErrors.branchName = 'Branch name is required';
    }

    if (!bankForm.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account holder name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(bankForm.accountHolderName)) {
      newErrors.accountHolderName = 'Account holder name should contain only letters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateWithdrawalAmount = () => {
    const newErrors = {};
    const amount = parseFloat(withdrawalAmount);

    if (!withdrawalAmount.trim()) {
      newErrors.withdrawalAmount = 'Withdrawal amount is required';
    } else if (isNaN(amount) || amount <= 0) {
      newErrors.withdrawalAmount = 'Please enter a valid amount';
    } else if (amount < 100) {
      newErrors.withdrawalAmount = 'Minimum withdrawal amount is ₹100';
    } else if (amount > withdrawableAmount) {
      newErrors.withdrawalAmount = `Insufficient withdrawable balance. Available: ₹${withdrawableAmount.toLocaleString()}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveBankDetails = async () => {
    if (!validateBankForm()) return;

    try {
      setIsSubmitting(true);
      const bankDetailsData = {
        ...bankForm,
        ifscCode: bankForm.ifscCode.toUpperCase(),
        addedAt: Date.now(),
        verified: false
      };

      await set(ref(database, `HTAMS/users/${currentUser.uid}/bankDetails`), bankDetailsData);
      setBankDetails(bankDetailsData);
      setShowBankForm(false);
      alert('Bank details saved successfully!');
    } catch (error) {
      console.error('Error saving bank details:', error);
      alert('Error saving bank details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateNetAmount = (grossAmount) => {
    let netAmount = grossAmount;

    // Calculate TDS if enabled
    if (tdsSettings.enabled && tdsSettings.percentage > 0) {
      const tdsAmount = (grossAmount * tdsSettings.percentage) / 100;
      netAmount = grossAmount - tdsAmount;
    }

    // Calculate Admin Charges if enabled
    if (adminChargesSettings.enabled && adminChargesSettings.percentage > 0) {
      const adminChargesAmount = (grossAmount * adminChargesSettings.percentage) / 100;
      netAmount = netAmount - adminChargesAmount;
    }

    return netAmount;
  };

  const submitWithdrawalRequest = async () => {
    if (!validateWithdrawalAmount()) return;

    try {
      setIsSubmitting(true);
      const grossAmount = parseFloat(withdrawalAmount);
      const userId = currentUser.uid;

      // Calculate TDS if enabled
      let tdsAmount = 0;
      let netAmount = grossAmount;

      if (tdsSettings.enabled && tdsSettings.percentage > 0) {
        tdsAmount = (grossAmount * tdsSettings.percentage) / 100;
        netAmount = grossAmount - tdsAmount;
      }

      // Calculate Admin Charges if enabled
      let adminChargesAmount = 0;

      if (adminChargesSettings.enabled && adminChargesSettings.percentage > 0) {
        adminChargesAmount = (grossAmount * adminChargesSettings.percentage) / 100;
        netAmount = netAmount - adminChargesAmount;
      }

      // DEDUCT GROSS AMOUNT IMMEDIATELY FROM WALLET
      const userRef = ref(database, `HTAMS/users/${userId}`);

      let newBalance = 0;
      let currentMySales = 0;

      await runTransaction(userRef, (userData) => {
        if (userData) {
          currentMySales = parseFloat(userData.MySales || 0);

          if (currentMySales < grossAmount) {
            throw new Error('Insufficient balance');
          }

          newBalance = currentMySales - grossAmount;
          userData.MySales = newBalance;
          userData.lastUpdated = Date.now();

          return userData;
        }
        return userData;
      });

      // Prepare transaction data with TDS and Admin Charges details
      let transactionData = {
        amount: grossAmount,
        grossAmount: grossAmount,
        tdsApplied: tdsSettings.enabled,
        tdsPercentage: tdsSettings.percentage,
        tdsAmount: tdsAmount,
        adminChargesApplied: adminChargesSettings.enabled,
        adminChargesPercentage: adminChargesSettings.percentage,
        adminChargesAmount: adminChargesAmount,
        netAmount: netAmount,
        type: 'withdrawal',
        paymentMethod: 'bank', // Bank transfer only
        amount: parseFloat(withdrawalAmount),
        grossAmount: parseFloat(withdrawalAmount),
        status: 'pending',
        timestamp: Date.now(),
        userName: userData?.name || 'Unknown',
        email: userData?.email || '',
        totalEarnings: currentMySales,
        previousWithdrawn: totalWithdrawn || 0,
        previousPending: pendingWithdrawals || 0
      };

      if (bankDetails) {
        transactionData.bankDetails = cleanObject(bankDetails);
      }
      // UPI functionality removed

      transactionData = cleanObject(transactionData);

      const newTransactionRef = push(ref(database, 'HTAMS/transactions'));
      const newTransactionId = newTransactionRef.key;

      const updates = {};
      updates[`HTAMS/transactions/${newTransactionId}`] = {
        ...transactionData,
        id: newTransactionId,
        userId
      };
      updates[`HTAMS/users/${userId}/transactions/${newTransactionId}`] = {
        ...transactionData,
        id: newTransactionId
      };

      await update(ref(database), updates);

      setWithdrawalAmount('');
      await fetchUserData(userId);
      await fetchTransactions(userId);

      let alertMessage = `Withdrawal request submitted successfully!\n\n`;
      alertMessage += `Gross Amount: ₹${grossAmount.toLocaleString()}\n`;

      if (tdsAmount > 0) {
        alertMessage += `TDS (${tdsSettings.percentage}%): ₹${tdsAmount.toLocaleString()}\n`;
      }

      if (adminChargesAmount > 0) {
        alertMessage += `Admin Charges (${adminChargesSettings.percentage}%): ₹${adminChargesAmount.toLocaleString()}\n`;
      }

      alertMessage += `Net Amount: ₹${netAmount.toLocaleString()}\n\n`;
      alertMessage += `₹${grossAmount.toLocaleString()} has been deducted from your wallet.`;

      alert(alertMessage);
    } catch (error) {
      console.error('Error submitting withdrawal request:', error);
      if (error.message === 'Insufficient balance') {
        alert('Insufficient wallet balance. Please check your available balance.');
      } else {
        alert('Error submitting withdrawal request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelWithdrawalRequest = async (transactionId) => {
    if (!window.confirm('Are you sure you want to cancel this withdrawal request? The amount will be refunded to your wallet.')) {
      return;
    }

    try {
      setIsSubmitting(true);
      const transaction = transactions.find(t => t.id === transactionId);

      if (transaction && transaction.status === 'pending') {
        const refundAmount = parseFloat(transaction.amount);
        const userId = currentUser.uid;

        // REFUND AMOUNT BACK TO WALLET
        const userRef = ref(database, `HTAMS/users/${userId}`);

        await runTransaction(userRef, (userData) => {
          if (userData) {
            const currentMySales = parseFloat(userData.MySales || 0);
            userData.MySales = currentMySales + refundAmount;
            userData.lastUpdated = Date.now();
            return userData;
          }
          return userData;
        });

        await update(ref(database, `HTAMS/users/${userId}/transactions/${transactionId}`), {
          status: 'cancelled',
          cancelledAt: Date.now(),
          refundedToWallet: true
        });

        await update(ref(database, `HTAMS/transactions/${transactionId}`), {
          status: 'cancelled',
          cancelledAt: Date.now(),
          refundedToWallet: true
        });

        await fetchUserData(userId);
        await fetchTransactions(userId);

        alert(`Withdrawal request cancelled successfully! ₹${refundAmount.toLocaleString()} has been refunded to your wallet.`);
      }
    } catch (error) {
      console.error('Error cancelling withdrawal request:', error);
      alert('Error cancelling withdrawal request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      case 'cancelled': return '#757575';
      default: return '#2196f3';
    }
  };

  if (loading) {
    return <div className="withdraw-loading">Loading...</div>;
  }

  if (!currentUser || !userData) {
    return <div className="withdraw-error">Please log in to access this page.</div>;
  }

  const totalEarnings = parseFloat(userData.MySales || 0);

  return (
    <div className="withdraw-container">
      <div className="withdraw-header">
        <h1>Withdraw Money</h1>

        <div className="earnings-summary">
          <div className="summary-card total-earnings">
            <h3>Total Earnings</h3>
            <div className="amount">₹{totalEarnings.toLocaleString()}</div>
            <small>From MySales</small>
          </div>

          <div className="summary-card total-withdrawn">
            <h3>Total Withdrawn</h3>
            <div className="amount">₹{totalWithdrawn.toLocaleString()}</div>
            <small>Approved withdrawals</small>
          </div>

          <div className="summary-card pending-withdrawals">
            <h3>Pending Withdrawals</h3>
            <div className="amount">₹{pendingWithdrawals.toLocaleString()}</div>
            <small>Under review</small>
          </div>

          <div className="summary-card withdrawable-amount">
            <h3>Withdrawable Amount</h3>
            <div className="amount highlighted">₹{withdrawableAmount.toLocaleString()}</div>
            <small>Available for withdrawal</small>
            {(tdsSettings.enabled || adminChargesSettings.enabled) && withdrawableAmount > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                <div>After deductions:
                  <strong style={{ color: '#10b981' }}> ₹{calculateNetAmount(withdrawableAmount).toLocaleString()}</strong>
                </div>
                {(tdsSettings.enabled || adminChargesSettings.enabled) && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#9ca3af' }}>
                    {tdsSettings.enabled && <span>TDS: {tdsSettings.percentage}% </span>}
                    {adminChargesSettings.enabled && <span>Admin: {adminChargesSettings.percentage}%</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="balance-breakdown">
          <h4>Balance Breakdown</h4>
          <div className="breakdown-details">
            <div className="breakdown-item">
              <span>Total Earnings (MySales):</span>
              <span>₹{totalEarnings.toLocaleString()}</span>
            </div>
            <div className="breakdown-item subtract">
              <span>- Total Withdrawn:</span>
              <span>₹{totalWithdrawn.toLocaleString()}</span>
            </div>
            <div className="breakdown-item subtract">
              <span>- Pending Withdrawals:</span>
              <span>₹{pendingWithdrawals.toLocaleString()}</span>
            </div>
            <div className="breakdown-item total">
              <span><strong>= Available for Withdrawal:</strong></span>
              <span><strong>₹{withdrawableAmount.toLocaleString()}</strong></span>
            </div>
          </div>
        </div>
      </div>

      {!bankDetails && (
        <div className="bank-details-prompt">
          <div className="prompt-content">
            <h3>Add Bank Details</h3>
            <p>Please add your bank details to proceed with withdrawals</p>
            <button
              className="btn-primary"
              onClick={() => setShowBankForm(true)}
            >
              Add Bank Details
            </button>
          </div>
        </div>
      )}

      {showBankForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Bank Details</h3>
              <button
                className="modal-close"
                onClick={() => setShowBankForm(false)}
              >
                ×
              </button>
            </div>
            <div className="form-container">
              <div className="form-group">
                <label>Account Number *</label>
                <input
                  type="text"
                  value={bankForm.accountNumber}
                  onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value.replace(/\D/g, '') })}
                  placeholder="Enter account number"
                  className={errors.accountNumber ? 'error' : ''}
                />
                {errors.accountNumber && <span className="error-text">{errors.accountNumber}</span>}
              </div>

              <div className="form-group">
                <label>Confirm Account Number *</label>
                <input
                  type="text"
                  value={bankForm.confirmAccountNumber}
                  onChange={(e) => setBankForm({ ...bankForm, confirmAccountNumber: e.target.value.replace(/\D/g, '') })}
                  placeholder="Re-enter account number"
                  className={errors.confirmAccountNumber ? 'error' : ''}
                />
                {errors.confirmAccountNumber && <span className="error-text">{errors.confirmAccountNumber}</span>}
              </div>

              <div className="form-group">
                <label>IFSC Code *</label>
                <input
                  type="text"
                  value={bankForm.ifscCode}
                  onChange={(e) => setBankForm({ ...bankForm, ifscCode: e.target.value.toUpperCase() })}
                  placeholder="Enter IFSC code"
                  className={errors.ifscCode ? 'error' : ''}
                />
                {errors.ifscCode && <span className="error-text">{errors.ifscCode}</span>}
              </div>

              <div className="form-group">
                <label>Bank Name *</label>
                <input
                  type="text"
                  value={bankForm.bankName}
                  onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                  placeholder="Enter bank name"
                  className={errors.bankName ? 'error' : ''}
                />
                {errors.bankName && <span className="error-text">{errors.bankName}</span>}
              </div>

              <div className="form-group">
                <label>Branch Name *</label>
                <input
                  type="text"
                  value={bankForm.branchName}
                  onChange={(e) => setBankForm({ ...bankForm, branchName: e.target.value })}
                  placeholder="Enter branch name"
                  className={errors.branchName ? 'error' : ''}
                />
                {errors.branchName && <span className="error-text">{errors.branchName}</span>}
              </div>

              <div className="form-group">
                <label>Account Holder Name *</label>
                <input
                  type="text"
                  value={bankForm.accountHolderName}
                  onChange={(e) => setBankForm({ ...bankForm, accountHolderName: e.target.value.replace(/[^a-zA-Z\s]/g, '') })}
                  placeholder="Enter account holder name"
                  className={errors.accountHolderName ? 'error' : ''}
                />
                {errors.accountHolderName && <span className="error-text">{errors.accountHolderName}</span>}
              </div>

              <div className="form-group">
                <label>Account Type *</label>
                <select
                  value={bankForm.accountType}
                  onChange={(e) => setBankForm({ ...bankForm, accountType: e.target.value })}
                >
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowBankForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveBankDetails}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Bank Details'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bankDetails && withdrawableAmount > 0 && (
        <div className="withdrawal-form">
          <h3>Create Withdrawal Request</h3>

          <div className="withdrawal-amount-section">
            <div className="form-group">
              <label>Withdrawal Amount *</label>
              <div className="amount-input-group">
                <span className="currency-symbol">₹</span>
                <input
                  type="number"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="100"
                  max={withdrawableAmount}
                  className={errors.withdrawalAmount ? 'error' : ''}
                />
              </div>
              {errors.withdrawalAmount && <span className="error-text">{errors.withdrawalAmount}</span>}
              <small className="helper-text">
                Minimum: ₹100 | Maximum: ₹{withdrawableAmount.toLocaleString()}
              </small>

              {/* TDS and Admin Charges Information */}
              {withdrawalAmount && parseFloat(withdrawalAmount) > 0 && (tdsSettings.enabled || adminChargesSettings.enabled) && (
                <div className="charges-info-box" style={{
                  marginTop: '15px',
                  padding: '15px',
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#92400e' }}>
                    💰 Charges Calculation
                  </h4>
                  <div style={{ fontSize: '13px', color: '#78350f' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>Gross Amount:</span>
                      <strong>₹{parseFloat(withdrawalAmount).toLocaleString()}</strong>
                    </div>

                    {tdsSettings.enabled && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#f59e0b' }}>
                        <span>TDS ({tdsSettings.percentage}%):</span>
                        <strong>- ₹{((parseFloat(withdrawalAmount) * tdsSettings.percentage) / 100).toLocaleString()}</strong>
                      </div>
                    )}

                    {adminChargesSettings.enabled && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', color: '#ef4444' }}>
                        <span>Admin Charges ({adminChargesSettings.percentage}%):</span>
                        <strong>- ₹{((parseFloat(withdrawalAmount) * adminChargesSettings.percentage) / 100).toLocaleString()}</strong>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #fbbf24', color: '#10b981', fontSize: '15px' }}>
                      <span><strong>Net Amount (You will receive):</strong></span>
                      <strong>₹{calculateNetAmount(parseFloat(withdrawalAmount)).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="quick-amounts">
              {[1000, 5000, 10000, Math.min(25000, withdrawableAmount)].map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className="quick-amount-btn"
                  onClick={() => setWithdrawalAmount(amount.toString())}
                  disabled={amount > withdrawableAmount}
                >
                  ₹{amount.toLocaleString()}
                </button>
              ))}
              {withdrawableAmount > 25000 && (
                <button
                  type="button"
                  className="quick-amount-btn"
                  onClick={() => setWithdrawalAmount(withdrawableAmount.toString())}
                >
                  Max (₹{withdrawableAmount.toLocaleString()})
                </button>
              )}
            </div>

            <div className="account-details" style={{ display: 'none' }}>
              <h4>Transfer Details</h4>
              <div className="details-card">
                <p><strong>Account Holder:</strong> {bankDetails.accountHolderName}</p>
                <p><strong>Account Number:</strong> ****{bankDetails.accountNumber.slice(-4)}</p>
                <p><strong>Bank:</strong> {bankDetails.bankName}</p>
                <p><strong>IFSC:</strong> {bankDetails.ifscCode}</p>
              </div>
              {/* UPI details removed */}
            </div>

            <button
              className="btn-primary withdrawal-submit"
              onClick={submitWithdrawalRequest}
              disabled={isSubmitting || !withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || parseFloat(withdrawalAmount) > withdrawableAmount}
            >
              {isSubmitting ? 'Processing...' : (
                (tdsSettings.enabled || adminChargesSettings.enabled) && withdrawalAmount && parseFloat(withdrawalAmount) > 0
                  ? `Submit Request (Net: ₹${calculateNetAmount(parseFloat(withdrawalAmount)).toLocaleString()})`
                  : 'Submit Withdrawal Request'
              )}
            </button>
          </div>
        </div>
      )}

      {bankDetails && withdrawableAmount <= 0 && (
        <div className="no-withdrawable-amount">
          <div className="info-card">
            <h3>No Withdrawable Amount</h3>
            <p>You have already withdrawn or requested all available earnings.</p>
            <div className="breakdown">
              <p><strong>Total Earnings:</strong> ₹{totalEarnings.toLocaleString()}</p>
              <p><strong>Total Withdrawn:</strong> ₹{totalWithdrawn.toLocaleString()}</p>
              <p><strong>Pending Withdrawals:</strong> ₹{pendingWithdrawals.toLocaleString()}</p>
              <p><strong>Available Balance:</strong> ₹{withdrawableAmount.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      <div className="transaction-history">
        <div className="history-header">
          <h3>Transaction History</h3>
          <button
            className="btn-secondary"
            onClick={() => fetchTransactions(currentUser.uid)}
          >
            Refresh
          </button>
        </div>

        {transactions.length > 0 ? (
          <div className="transactions-list">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="transaction-item">
                <div className="transaction-info">
                  <div className="transaction-amount">
                    ₹{parseFloat(transaction.netAmount).toLocaleString()}
                  </div>
                  <div className="transaction-details">
                    <p><strong>Mode:</strong> {transaction.mode?.toUpperCase()}</p>
                    <p><strong>Date:</strong> {new Date(transaction.requestedAt).toLocaleDateString()}</p>
                    <p><strong>Status:</strong>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(transaction.status) }}
                      >
                        {transaction.status?.toUpperCase()}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="transaction-actions">
                  {transaction.status === 'pending' && (
                    <button
                      className="btn-danger"
                      onClick={() => cancelWithdrawalRequest(transaction.id)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setSelectedTransaction(transaction);
                      setShowTransactionModal(true);
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-transactions">
            <p>No transactions found.</p>
          </div>
        )}
      </div>

      {showTransactionModal && selectedTransaction && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Transaction Details</h3>
              <button
                className="modal-close"
                onClick={() => setShowTransactionModal(false)}
              >
                ×
              </button>
            </div>
            <div className="transaction-details-modal">
              <div className="detail-row">
                <span>Transaction ID:</span>
                <span>{selectedTransaction.id}</span>
              </div>
              <div className="detail-row">
                <span>Amount:</span>
                <span>₹{parseFloat(selectedTransaction.amount).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <span>Mode:</span>
                <span>{selectedTransaction.mode?.toUpperCase()}</span>
              </div>
              <div className="detail-row">
                <span>Status:</span>
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(selectedTransaction.status) }}
                >
                  {selectedTransaction.status?.toUpperCase()}
                </span>
              </div>
              <div className="detail-row">
                <span>Requested Date:</span>
                <span>{new Date(selectedTransaction.requestedAt).toLocaleString()}</span>
              </div>
              {selectedTransaction.deductedFromWallet && (
                <div className="detail-row">
                  <span>Deducted from Wallet:</span>
                  <span>Yes</span>
                </div>
              )}
              {selectedTransaction.adminChargesApplied && !selectedTransaction.tdsApplied && (
                <>
                  <div className="detail-row">
                    <span>Gross Amount:</span>
                    <span style={{ fontWeight: '600' }}>₹{parseFloat(selectedTransaction.grossAmount || selectedTransaction.amount).toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Admin Charges ({selectedTransaction.adminChargesPercentage}%):</span>
                    <span style={{ color: '#ef4444', fontWeight: '600' }}>₹{parseFloat(selectedTransaction.adminChargesAmount).toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Net Amount:</span>
                    <span style={{ color: '#10b981', fontWeight: '700', fontSize: '16px' }}>₹{parseFloat(selectedTransaction.netAmount).toLocaleString()}</span>
                  </div>
                </>
              )}
              {selectedTransaction.tdsApplied && (
                <>
                  <div className="detail-row">
                    <span>Gross Amount:</span>
                    <span style={{ fontWeight: '600' }}>₹{parseFloat(selectedTransaction.grossAmount || selectedTransaction.amount).toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>TDS ({selectedTransaction.tdsPercentage}%):</span>
                    <span style={{ color: '#f59e0b', fontWeight: '600' }}>₹{parseFloat(selectedTransaction.tdsAmount).toLocaleString()}</span>
                  </div>
                  {selectedTransaction.adminChargesApplied && (
                    <div className="detail-row">
                      <span>Admin Charges ({selectedTransaction.adminChargesPercentage}%):</span>
                      <span style={{ color: '#ef4444', fontWeight: '600' }}>₹{parseFloat(selectedTransaction.adminChargesAmount).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span>Net Amount:</span>
                    <span style={{ color: '#10b981', fontWeight: '700', fontSize: '16px' }}>₹{parseFloat(selectedTransaction.netAmount).toLocaleString()}</span>
                  </div>
                </>
              )}
              {selectedTransaction.approvedAt && (
                <div className="detail-row">
                  <span>Approved Date:</span>
                  <span>{new Date(selectedTransaction.approvedAt).toLocaleString()}</span>
                </div>
              )}
              {selectedTransaction.cancelledAt && (
                <div className="detail-row">
                  <span>Cancelled Date:</span>
                  <span>{new Date(selectedTransaction.cancelledAt).toLocaleString()}</span>
                </div>
              )}
              {selectedTransaction.refundedToWallet && (
                <div className="detail-row">
                  <span>Refunded to Wallet:</span>
                  <span>Yes</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`
.withdraw-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.withdraw-header {
  text-align: center;
  margin-bottom: 30px;
}

.withdraw-header h1 {
  color: #333;
  margin-bottom: 20px;
  font-size: 2rem;
}

.balance-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  margin-bottom: 20px;
}

.balance-card h3 {
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  opacity: 0.9;
}

.balance-amount {
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0;
}

.bank-details-prompt {
  background: #f8f9ff;
  border: 2px dashed #667eea;
  border-radius: 15px;
  padding: 40px;
  text-align: center;
  margin-bottom: 30px;
}

.prompt-content h3 {
  color: #333;
  margin-bottom: 10px;
}

.prompt-content p {
  color: #666;
  margin-bottom: 20px;
}

.withdrawal-form {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.withdrawal-form h3 {
  color: #333;
  margin-bottom: 25px;
  text-align: center;
}

.payment-modes {
  margin-bottom: 30px;
}

.mode-selector {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.mode-btn {
  padding: 12px 30px;
  border: 2px solid #667eea;
  background: white;
  color: #667eea;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.mode-btn:hover {
  background: #667eea;
  color: white;
}

.mode-btn.active {
  background: #667eea;
  color: white;
}

.upi-form {
  background: #f8f9ff;
  padding: 25px;
  border-radius: 10px;
  margin-bottom: 25px;
}

.upi-form h4 {
  color: #333;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #333;
  margin-bottom: 8px;
  font-weight: 600;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
}

.form-group input.error {
  border-color: #f44336;
}

.error-text {
  color: #f44336;
  font-size: 0.875rem;
  margin-top: 5px;
  display: block;
}

.withdrawal-amount-section {
  background: #f8f9ff;
  padding: 25px;
  border-radius: 10px;
  margin-top: 25px;
}

.amount-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-symbol {
  position: absolute;
  left: 15px;
  color: #667eea;
  font-weight: bold;
  z-index: 1;
}

.amount-input-group input {
  padding-left: 35px;
}

.helper-text {
  color: #666;
  font-size: 0.875rem;
  margin-top: 5px;
  display: block;
}

.quick-amounts {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin: 20px 0;
}

.quick-amount-btn {
  padding: 8px 16px;
  border: 1px solid #667eea;
  background: white;
  color: #667eea;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
}

.quick-amount-btn:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

.quick-amount-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.account-details {
  margin: 25px 0;
}

.account-details h4 {
  color: #333;
  margin-bottom: 15px;
}

.details-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #e1e5e9;
}

.details-card p {
  margin: 8px 0;
  color: #555;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 16px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  padding: 10px 25px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-secondary:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

.btn-danger {
  background: #f44336;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 0.875rem;
}

.btn-danger:hover:not(:disabled) {
  background: #d32f2f;
}

.withdrawal-submit {
  width: 100%;
  margin-top: 25px;
  padding: 15px;
  font-size: 18px;
}

/* Earnings Summary Section */
.earnings-summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 30px;
}

.summary-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  display: grid;
  gap:1px;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.12);
}

.summary-card h3 {
  color: #666;
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.summary-card .amount {
  font-size: 1.6rem;
  font-weight: bold;
  margin: 8px 0;
  color: #333;
}

.summary-card small {
  color: #888;
  font-size: 0.8rem;
}

.total-earnings {
  border-color: #2196f3;
}

.total-earnings .amount {
  color: #2196f3;
}

.total-withdrawn {
  border-color: #ff9800;
}

.total-withdrawn .amount {
  color: #ff9800;
}

.pending-withdrawals {
  border-color: #9c27b0;
}

.pending-withdrawals .amount {
  color: #9c27b0;
}

.withdrawable-amount {
  border-color: #4caf50;
  background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
}

.withdrawable-amount .amount.highlighted {
  color: #4caf50;
  font-size: 1.8rem;
}

.no-withdrawable-amount {
  background: #fff3e0;
  border: 2px dashed #ff9800;
  border-radius: 15px;
  padding: 40px;
  text-align: center;
  margin-bottom: 30px;
}

.info-card {
  max-width: 500px;
  margin: 0 auto;
}

.info-card h3 {
  color: #ff9800;
  margin-bottom: 15px;
}

.info-card p {
  color: #666;
  margin-bottom: 20px;
}

.breakdown {
  background: white;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #e1e5e9;
}

.breakdown p {
  margin: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-breakdown {
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

.balance-breakdown h4 {
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.breakdown-details {
  max-width: 400px;
  margin: 0 auto;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
}

.breakdown-item.subtract {
  color: #f44336;
}

.breakdown-item.total {
  border-top: 2px solid #4caf50;
  border-bottom: none;
  color: #4caf50;
  font-size: 1.1rem;
  margin-top: 15px;
  padding-top: 15px;
}

.breakdown-item:last-child {
  border-bottom: none;
}

/* Transaction History Section */
.transaction-history {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.history-header h3 {
  color: #333;
  margin: 0;
  font-size: 1.2rem;
}

.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transaction-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 1px solid #e1e5e9;
  border-radius: 10px;
  margin-bottom: 0;
  transition: all 0.3s ease;
}

.transaction-item:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.transaction-info {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.transaction-amount {
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
  min-width: 100px;
}

.transaction-details {
  flex: 1;
}

.transaction-details p {
  margin: 3px 0;
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
}

.transaction-details p strong {
  color: #333;
  font-size: 0.8rem;
  min-width: 50px;
}

.status-badge {
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 5px;
  display: inline-block;
}

.transaction-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.transaction-actions .btn-secondary,
.transaction-actions .btn-danger {
  padding: 8px 16px;
  font-size: 0.85rem;
  border-radius: 6px;
  white-space: nowrap;
}

.no-transactions {
  text-align: center;
  padding: 30px;
  color: #666;
  font-size: 0.95rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 15px;
  padding: 0;

  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #e1e5e9;
  background: #f8f9ff;
  border-radius: 15px 15px 0 0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #e1e5e9;
  color: #333;
}

.form-container {
  padding: 30px;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
}

.transaction-details-modal {
  padding: 30px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row span:first-child {
  font-weight: 600;
  color: #333;
}

.detail-row span:last-child {
  color: #666;
}

.withdraw-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #666;
}

.withdraw-error {
  text-align: center;
  color: #f44336;
  font-size: 18px;
  padding: 40px;
}

/* Responsive Design - Tablet */
@media (max-width: 768px) {
  .withdraw-container {
    padding: 15px;
  }

  .balance-amount {
    font-size: 2rem;
  }

  .mode-selector {
    flex-direction: column;
  }

  /* Earnings Summary - Tablet */
  .earnings-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .summary-card {
    padding: 16px;
  }

  .summary-card h3 {
    font-size: 0.85rem;
  }

  .summary-card .amount {
    font-size: 1.4rem;
  }

  .withdrawable-amount .amount.highlighted {
    font-size: 1.6rem;
  }

  .summary-card small {
    font-size: 0.75rem;
  }

  /* Balance Breakdown - Tablet */
  .balance-breakdown {
    padding: 20px;
  }

  .breakdown-item {
    font-size: 0.9rem;
  }

  .breakdown-item.total {
    font-size: 1rem;
  }

  /* Transaction History - Tablet */
  .transaction-history {
    padding: 15px;
  }

  .history-header h3 {
    font-size: 1.1rem;
  }

  .history-header .btn-secondary {
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .transaction-item {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .transaction-info {
    flex-direction: column;
    gap: 10px;
    width: 100%;
    align-items: flex-start;
  }

  .transaction-amount {
    font-size: 1.2rem;
  }

  .transaction-details {
    width: 100%;
  }

  .transaction-details p {
    font-size: 0.8rem;
  }

  .transaction-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .transaction-actions .btn-secondary,
  .transaction-actions .btn-danger {
    flex: 1;
    padding: 8px 12px;
    font-size: 0.8rem;
  }

  .quick-amounts {
    justify-content: center;
  }

  .form-actions {
    flex-direction: column;
  }

  .modal-content {
    width: 100%;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}

/* Responsive Design - Mobile */
@media (max-width: 480px) {
  .balance-amount {
    font-size: 1.5rem;
  }

  .withdrawal-form,
  .transaction-history {
    padding: 20px;
  }

  .form-container {
    padding: 20px;
  }

  .transaction-details-modal {
    padding: 20px;
  }

  /* Earnings Summary - Mobile */
  .earnings-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .summary-card {
    padding: 12px;
  }

  .summary-card h3 {
    font-size: 0.8rem;
    margin-bottom: 8px;
  }

  .summary-card .amount {
    font-size: 1.2rem;
    margin: 6px 0;
  }

  .withdrawable-amount .amount.highlighted {
    font-size: 1.4rem;
  }

  .summary-card small {
    font-size: 0.7rem;
  }

  /* Transaction History - Mobile */
  .transaction-history {
    padding: 12px;
  }

  .history-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .history-header .btn-secondary {
    width: 100%;
  }

  .transaction-item {
    padding: 10px;
  }

  .transaction-amount {
    font-size: 1.1rem;
  }

  .transaction-details p {
    font-size: 0.75rem;
  }

  .transaction-details p strong {
    font-size: 0.75rem;
    min-width: 45px;
  }

  .status-badge {
    font-size: 0.65rem;
    padding: 2px 6px;
  }

  .transaction-actions {
    flex-direction: column;
    gap: 8px;
  }

  .transaction-actions .btn-secondary,
  .transaction-actions .btn-danger {
    width: 100%;
  }
}

/* Responsive Design - Small Mobile */
@media (max-width: 360px) {

  /* Earnings Summary - Small Mobile */
  .earnings-summary {
    gap: 8px;
  }

  .summary-card {
    padding: 10px;
  }

  .summary-card h3 {
    font-size: 0.75rem;
  }

  .summary-card .amount {
    font-size: 1.1rem;
  }

  .withdrawable-amount .amount.highlighted {
    font-size: 1.3rem;
  }

  /* Transaction History - Small Mobile */
  .transaction-amount {
    font-size: 1rem;
    min-width: 90px;
  }

  .transaction-details p {
    font-size: 0.7rem;
  }

  .transaction-details p strong {
    font-size: 0.7rem;
  }
}

/* Responsive Design - Extra Small Mobile (300px - 350px) */
@media (max-width: 380px) {
  .withdraw-container {
    padding: 10px;
  }

  .withdraw-header h1 {
    font-size: 1.5rem;
    margin-bottom: 20px;
  }

  /* Stack Summary Cards on very small screens */
  .earnings-summary {
    grid-template-columns: 1fr;
    /* Switch to single column */
    gap: 12px;
  }

  .summary-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    text-align: left;
  }

  .summary-card h3 {
    margin-bottom: 4px;
    font-size: 0.9rem;
  }

  .summary-card .amount {
    margin: 0;
    font-size: 1.3rem;
  }

  .summary-card small {
    display: none;
    /* Hide small text to save space if needed, or keep it */
  }

  .summary-card div {
    text-align: right;
  }

  /* Adjust withdrawable card specially if needed */
  .withdrawable-amount {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .withdrawable-amount div {
    text-align: left;
    width: 100%;
  }

  .balance-breakdown {
    padding: 15px;
  }

  .breakdown-item {
    font-size: 0.8rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .breakdown-item span:last-child {
    align-self: flex-end;
    font-weight: 600;
  }
}
      `}</style>
    </div>
  );
};

export default WithdrawMoney;
